import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getStripeEnvironment } from "@/lib/stripe";
import { subscribeToTable } from "@/lib/realtimeChannels";
import type { User } from "@supabase/supabase-js";
import { useSubscription } from "@/hooks/useSubscription";

/**
 * Returns whether the given user has access to a specific premium item,
 * either via active subscription or a one-time purchase.
 */
export function useUnlock(
  user: User | null,
  purchaseType: "premium_guide" | "digital_resource",
  entityId: string | null | undefined
) {
  const { isActive: hasSub, loading: subLoading } = useSubscription(user);
  const [hasOneTime, setHasOneTime] = useState(false);
  const [loading, setLoading] = useState(true);

  const refetch = async () => {
    if (!user || !entityId) {
      setHasOneTime(false);
      setLoading(false);
      return;
    }
    const { count } = await supabase
      .from("purchases" as any)
      .select("id", { count: "exact", head: true })
      .eq("user_id", user.id)
      .eq("purchase_type", purchaseType)
      .eq("entity_id", entityId)
      .eq("environment", getStripeEnvironment());
    setHasOneTime((count ?? 0) > 0);
    setLoading(false);
  };

  useEffect(() => {
    refetch();
    if (!user || !entityId) return;
    const unsubscribe = subscribeToTable(
      {
        table: "purchases",
        event: "INSERT",
        filter: `user_id=eq.${user.id}`,
      },
      () => refetch()
    );
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, entityId, purchaseType]);

  return {
    isUnlocked: hasSub || hasOneTime,
    hasSub,
    hasOneTime,
    loading: loading || subLoading,
    refetch,
  };
}
